import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import { addDays, daysBetween } from '@/core/time/dayKey'

/**
 * Maps a flat virtual-row index onto (dayKey, slotIndex) across a range of days.
 *
 * This exists because days are not all the same length. A window spanning a DST
 * transition contains days of 46, 48 and 50 slots -- and on Lord Howe Island,
 * 47 and 49. Any arithmetic of the form `index / 48` is therefore wrong, and
 * wrong in a way that silently misaligns the entire timeline below the
 * transition rather than throwing.
 *
 * Pure and synchronous, so the awkward cases are cheap to test.
 */

export interface TimelineDay {
  dayKey: number
  slotCount: number
  /** Flat index of this day's slot 0. */
  firstIndex: number
}

export interface RowHeights {
  /** Height of one block row. */
  row: number
  /** Height of the divider drawn above the first row of each day. */
  divider: number
}

export interface TimelineWindow {
  readonly days: readonly TimelineDay[]
  readonly firstDayKey: number
  readonly lastDayKey: number
  readonly totalRows: number

  /** null when the index falls outside the window. */
  slotAt(index: number): BlockSlot | null
  dayIndexForRow(index: number): number
  dayAt(index: number): TimelineDay | null

  /** -1 when the slot falls outside the window. */
  indexOf(slot: BlockSlot): number

  isFirstSlotOfDay(index: number): boolean

  /** Height of a virtual item, including its divider when it starts a day. */
  rowSize(index: number, heights: RowHeights): number

  /** Top of the virtual item, i.e. of the divider when there is one. */
  rowOffset(index: number, heights: RowHeights): number

  /** Top of the block itself, below any divider. */
  blockOffset(index: number, heights: RowHeights): number

  /**
   * The row at a vertical position, for pointer hit-testing during a drag.
   * Clamped to the window rather than returning -1, so dragging past either
   * end selects to that end instead of losing the drag.
   */
  indexAtOffset(y: number, heights: RowHeights): number

  totalHeight(heights: RowHeights): number
}

/** Guards against a range so large that building it would stall the UI. */
export const MAX_WINDOW_DAYS = 3000

export function buildTimelineWindow(
  grid: BlockGrid,
  fromDayKey: number,
  toDayKey: number,
): TimelineWindow {
  const span = daysBetween(fromDayKey, toDayKey)
  if (span < 0) throw new Error(`Window end ${toDayKey} precedes start ${fromDayKey}`)
  if (span + 1 > MAX_WINDOW_DAYS) {
    throw new Error(`Window of ${span + 1} days exceeds the ${MAX_WINDOW_DAYS}-day limit`)
  }

  const days: TimelineDay[] = []
  let cursor = fromDayKey
  let firstIndex = 0

  for (let i = 0; i <= span; i++) {
    const slotCount = grid.slotsPerDay(cursor)
    days.push({ dayKey: cursor, slotCount, firstIndex })
    firstIndex += slotCount
    cursor = addDays(cursor, 1)
  }

  const totalRows = firstIndex

  /** Binary search for the day containing a flat index. */
  function dayIndexForRow(index: number): number {
    if (index < 0 || index >= totalRows) return -1

    let lo = 0
    let hi = days.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const day = days[mid]
      if (!day) break
      if (index < day.firstIndex) hi = mid - 1
      else if (index >= day.firstIndex + day.slotCount) lo = mid + 1
      else return mid
    }
    return -1
  }

  function dayAt(index: number): TimelineDay | null {
    const i = dayIndexForRow(index)
    return i === -1 ? null : (days[i] ?? null)
  }

  function slotAt(index: number): BlockSlot | null {
    const day = dayAt(index)
    if (!day) return null
    return { dayKey: day.dayKey, slotIndex: index - day.firstIndex }
  }

  function indexOf(slot: BlockSlot): number {
    const offset = daysBetween(fromDayKey, slot.dayKey)
    if (offset < 0 || offset >= days.length) return -1

    const day = days[offset]
    if (!day) return -1
    if (slot.slotIndex < 0 || slot.slotIndex >= day.slotCount) return -1

    return day.firstIndex + slot.slotIndex
  }

  function isFirstSlotOfDay(index: number): boolean {
    const day = dayAt(index)
    return day !== null && day.firstIndex === index
  }

  function rowSize(index: number, heights: RowHeights): number {
    return heights.row + (isFirstSlotOfDay(index) ? heights.divider : 0)
  }

  /**
   * Dividers strictly above item `index`: one per day that starts before it.
   * A day whose first row IS this index has not contributed yet -- its divider
   * lives inside this very item.
   */
  function dividersAbove(index: number): number {
    const dayIdx = dayIndexForRow(index)
    if (dayIdx === -1) return 0
    const day = days[dayIdx]
    if (!day) return 0
    return day.firstIndex === index ? dayIdx : dayIdx + 1
  }

  function rowOffset(index: number, heights: RowHeights): number {
    return index * heights.row + dividersAbove(index) * heights.divider
  }

  function blockOffset(index: number, heights: RowHeights): number {
    return rowOffset(index, heights) + (isFirstSlotOfDay(index) ? heights.divider : 0)
  }

  function totalHeight(heights: RowHeights): number {
    return totalRows * heights.row + days.length * heights.divider
  }

  /**
   * Top of a day's block, i.e. of its divider.
   *
   * Exactly `d` dividers precede day `d`, one per earlier day, so this is O(1)
   * with no cumulative table to keep in step with the current row height.
   */
  function dayTop(dayIndex: number, heights: RowHeights): number {
    const day = days[dayIndex]
    if (!day) return 0
    return day.firstIndex * heights.row + dayIndex * heights.divider
  }

  function indexAtOffset(y: number, heights: RowHeights): number {
    if (totalRows === 0) return -1
    if (y <= 0) return 0

    // Binary search for the day containing this pixel.
    let lo = 0
    let hi = days.length - 1
    let found = days.length - 1

    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      if (dayTop(mid, heights) <= y) {
        found = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }

    const day = days[found]
    if (!day) return totalRows - 1

    // A pointer on the divider belongs to the first block of that day.
    const local = y - dayTop(found, heights) - heights.divider
    const withinDay = local <= 0 ? 0 : Math.floor(local / heights.row)
    const clamped = Math.min(withinDay, day.slotCount - 1)

    return Math.min(day.firstIndex + clamped, totalRows - 1)
  }

  return {
    days,
    firstDayKey: fromDayKey,
    lastDayKey: toDayKey,
    totalRows,
    slotAt,
    dayIndexForRow,
    dayAt,
    indexOf,
    isFirstSlotOfDay,
    rowSize,
    rowOffset,
    blockOffset,
    indexAtOffset,
    totalHeight,
  }
}

/**
 * Vertical position of an instant within the window, in pixels.
 *
 * Interpolates within the slot so the now marker moves smoothly rather than
 * jumping between block boundaries. Returns null when the instant lies outside.
 */
export function offsetForInstant(
  window: TimelineWindow,
  grid: BlockGrid,
  ms: number,
  heights: RowHeights,
): number | null {
  const slot = grid.slotForInstant(ms)
  const index = window.indexOf(slot)
  if (index === -1) return null

  const slotStart = grid.startOfSlot(slot.dayKey, slot.slotIndex)
  const fraction = Math.min(1, Math.max(0, (ms - slotStart) / grid.slotDurationMs))

  return window.blockOffset(index, heights) + fraction * heights.row
}
